// src/models/ownerModel.js - Frontend Owner Model
import api from '../services/api';




// Owner Management Functions

// Get all owners with filters
export const getAllOwners = async (filters = {}) => {
    try {
        const response = await api.get('/owners', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching owners' };
    }
};

// Get owner by ID
export const getOwner = async (id) => {
    try {
        const response = await api.get(`/owners/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching owner' };
    }
};

// Create new owner
export const createOwner = async (ownerData) => {
    try {
        const response = await api.post('/owners', ownerData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error creating owner' };
    }
};

// Update owner
export const updateOwner = async (id, ownerData) => {
    try {
        const response = await api.put(`/owners/${id}`, ownerData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating owner' };
    }
};

// Delete owner
export const deleteOwner = async (id) => {
    try {
        const response = await api.delete(`/owners/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error deleting owner' };
    }
};

// Search owners
export const searchOwners = async (searchTerm) => {
    try {
        const response = await api.get(`/owners/search?q=${encodeURIComponent(searchTerm)}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error searching owners' };
    }
};

// Get owner statistics
export const getOwnerStats = async () => {
    try {
        const response = await api.get('/owners/stats');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching owner stats' };
    }
};

// Get owners with overdue payments
export const getOwnersWithOverduePayments = async () => {
    try {
        const response = await api.get('/owners/overdue');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching overdue owners' };
    }
};

// Property Management Functions

// Get owner's properties
export const getOwnerProperties = async (ownerId) => {
    try {
        const response = await api.get(`/owners/${ownerId}/properties`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching owner properties' };
    }
};

// Payment Management Functions

// Get owner's payments
export const getOwnerPayments = async (ownerId, options = {}) => {
    try {
        const response = await api.get(`/owners/${ownerId}/payments`, { params: options });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching owner payments' };
    }
};

// Get owner's outstanding fees
export const getOwnerOutstandingFees = async (ownerId) => {
    try {
        const response = await api.get(`/owners/${ownerId}/outstanding`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching outstanding fees' };
    }
};

// Reminder Management Functions

// Send payment reminder
export const sendPaymentReminder = async (ownerId, reminderData) => {
    try {
        const response = await api.post(`/owners/${ownerId}/reminders`, reminderData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error sending reminder' };
    }
};

// Get owner's payment reminders
export const getOwnerReminders = async (ownerId) => {
    try {
        const response = await api.get(`/owners/${ownerId}/reminders`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching reminders' };
    }
};

// Helper Functions

// Format owner display name
export const formatOwnerDisplayName = (owner) => {
    return owner.name || `${owner.first_name} ${owner.last_name}`;
};

// Format phone number
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    // Handle Iraqi phone numbers
    if (phone.startsWith('+964')) {
        return phone.replace(/(\+964)(\d{3})(\d{3})(\d{4})/, '$1 $2-$3-$4');
    }

    // Handle other formats
    return phone;
};

// Validate owner data
export const validateOwnerData = (ownerData) => {
    const errors = {};

    if (!ownerData.name?.trim()) {
        errors.name = 'Name is required';
    }

    if (!ownerData.phone?.trim()) {
        errors.phone = 'Phone number is required';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Get owner status color
export const getOwnerStatusColor = (isActive, isDarkMode) => {
    if (isActive) {
        return isDarkMode
            ? 'bg-green-900 bg-opacity-20 text-green-400'
            : 'bg-green-100 text-green-800';
    } else {
        return isDarkMode
            ? 'bg-red-900 bg-opacity-20 text-red-400'
            : 'bg-zinc-100 text-red-800';
    }
};

// Calculate owner summary statistics
export const calculateOwnerSummary = (owner) => {
    const totalProperties = owner.total_apartments || 0;
    const occupiedProperties = owner.occupied_apartments || 0;
    const vacantProperties = owner.vacant_apartments || 0;

    // Calculate occupancy rate
    const occupancyRate = totalProperties > 0
        ? Math.round((occupiedProperties / totalProperties) * 100)
        : 0;

    // Calculate financial metrics (placeholders if real data not available)
    const monthlyIncome = owner.monthly_income || 0;
    const totalServiceFees = owner.total_service_fees || 0;
    const totalPotentialRent = owner.total_potential_rent || 0;

    return {
        totalProperties,
        occupiedProperties,
        vacantProperties,
        occupancyRate,
        monthlyIncome,
        totalServiceFees,
        totalPotentialRent
    };
};

// Update owner status
export const updateOwnerStatus = async (id, isActive) => {
    try {
        const response = await api.put(`/owners/${id}/status`, { isActive });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating owner status' };
    }
};

// Country options for dropdown
export const COUNTRY_OPTIONS = [
    { value: 'Iraq', label: 'Iraq' },
    { value: 'UAE', label: 'United Arab Emirates' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'Kuwait', label: 'Kuwait' },
    { value: 'Jordan', label: 'Jordan' },
    { value: 'Lebanon', label: 'Lebanon' },
    { value: 'Turkey', label: 'Turkey' }
];

// Format owner contact information
export const formatOwnerContact = (owner) => {
    const parts = [];
    if (owner.phone) parts.push(owner.phone);
    if (owner.email) parts.push(owner.email);
    return parts.join(' • ');
};

export default {
    getAllOwners,
    getOwner,
    createOwner,
    updateOwner,
    deleteOwner,
    searchOwners,
    getOwnerStats,
    getOwnersWithOverduePayments,
    getOwnerProperties,
    getOwnerPayments,
    getOwnerOutstandingFees,
    sendPaymentReminder,
    getOwnerReminders,
    formatOwnerDisplayName,
    formatPhoneNumber,
    validateOwnerData,
    formatCurrency,
    getOwnerStatusColor,
    calculateOwnerSummary,
    updateOwnerStatus,
    COUNTRY_OPTIONS,
    formatOwnerContact
};